import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getPost, BLOG_POSTS } from "@/data/blog";
import { useSEO } from "@/lib/useSEO";
import { SITE_URL as SITE } from "@/lib/site";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  useSEO(
    post
      ? {
          title: post.metaTitle,
          description: post.metaDescription,
          keywords: post.keywords,
          path: `/blog/${post.slug}`,
          image: `${SITE}/og-image.jpg`,
          jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BlogPosting",
                headline: post.title,
                description: post.metaDescription,
                datePublished: post.date,
                dateModified: post.date,
                author: { "@type": "Organization", name: "Endasphere" },
                publisher: { "@type": "Organization", name: "Endasphere", logo: { "@type": "ImageObject", url: `${SITE}/icon.jpg` } },
                mainEntityOfPage: `${SITE}/blog/${post.slug}`,
                image: `${SITE}/og-image.jpg`,
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
                  { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
                  { "@type": "ListItem", position: 3, name: post.title, item: `${SITE}/blog/${post.slug}` },
                ],
              },
            ],
          },
        }
      : { title: "Endasphere Blog", path: `/blog/${slug}` }
  );

  if (!post) return <Navigate to="/blog" replace />;

  const more = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <article data-testid={`blog-post-${post.slug}`}>
      <section className="relative pt-[140px] lg:pt-[180px] pb-16">
        <div className="absolute inset-0">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/85 via-[#05050A]/92 to-[#05050A]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
          <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 mb-8" aria-label="Breadcrumb">
            <Link to="/blog" className="hover:text-[#22D3EE] transition-colors flex items-center gap-1.5" data-testid="breadcrumb-blog">
              <ArrowLeft className="w-3.5 h-3.5" /> Blog
            </Link>
            <span className="text-slate-700">/</span>
            <Link to={`/${post.serviceSlug}`} className="hover:text-[#22D3EE] transition-colors" style={{ color: post.accent }}>{post.serviceNav}</Link>
          </nav>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-5">
              <span>{fmt(post.date)}</span><span className="text-slate-700">·</span><span>{post.readTime}</span>
            </div>
            <h1 className="font-display font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-[1.02]">
              {post.title}
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">{post.excerpt}</p>
          </motion.div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="space-y-10">
            {post.sections.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="font-display font-700 text-2xl sm:text-3xl tracking-tight text-white mb-4">{s.h}</h2>
                <p className="text-base text-slate-300 leading-relaxed">{s.p}</p>
              </motion.div>
            ))}
          </div>

          {/* Service CTA */}
          <div className="mt-14 rounded-2xl border border-white/10 bg-[#0A0A12] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3 className="font-display font-700 text-xl text-white tracking-tight">Explore {post.serviceNav}</h3>
              <p className="mt-2 text-sm text-slate-400">See how Endasphere puts this into practice.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to={`/${post.serviceSlug}`} data-testid="post-service-link" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-500 uppercase tracking-[0.12em] text-white hover:border-[#22D3EE] hover:text-[#22D3EE] transition-colors duration-300">
                View service
              </Link>
              <Link to="/#contact" className="group inline-flex items-center gap-2 rounded-full bg-[#22D3EE] px-6 py-3 text-xs font-700 uppercase tracking-[0.12em] text-[#05050A] hover:bg-white transition-colors duration-300">
                Request demo <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* More reading */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-display font-900 text-2xl sm:text-3xl tracking-tighter text-white mb-10">More reading</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {more.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`more-post-${p.slug}`} className="group rounded-2xl border border-white/10 bg-[#0A0A12] p-7 hover:border-[#22D3EE]/40 transition-colors duration-300">
                <span className="text-[11px] uppercase tracking-[0.15em]" style={{ color: p.accent }}>{p.serviceNav}</span>
                <h3 className="mt-4 font-display font-700 text-lg text-white tracking-tight leading-snug group-hover:text-[#22D3EE] transition-colors duration-300">{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
